import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { OrganizationProvider, useOrganization } from '../../../context/OrganizationContext';
import { useUser } from '../../../context/UserContext';
import { getAllSchedules, deleteSchedule, getScheduleRunHistory } from '../../../services/scheduleService';
import Sidebar from '../common/sidebar';
import TopBar from '../common/topbar';
import { Calendar, Clock, Trash2, AlertCircle, CheckCircle, Pause, MoreVertical, ArrowRight, Bot, ChevronDown, ChevronUp } from 'lucide-react';

const AlertsPageContent = () => {
  const { organizationId } = useParams();
  const { organizationData, fetchOrganizationData } = useOrganization();
  const { userInfo } = useUser();
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedScheduleId, setExpandedScheduleId] = useState(null);
  const [runHistory, setRunHistory] = useState({});
  const [loadingRuns, setLoadingRuns] = useState({});

  useEffect(() => {
    if (organizationId) {
      fetchOrganizationData(organizationId);
      fetchSchedules();
    }
  }, [organizationId, fetchOrganizationData]);

  const fetchSchedules = async () => {
    if (!organizationId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getAllSchedules(organizationId);
      const schedulesData = response?.data?.data || [];
      setSchedules(schedulesData);
    } catch (error) {
      // Error fetching schedules
      setError(error.response?.data?.message || 'Failed to fetch schedules');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    
    try {
      await deleteSchedule(organizationId, scheduleId);
      setSchedules(prev => prev.filter(s => s._id !== scheduleId));
    } catch (error) {
      // Error deleting schedule
      alert('Failed to delete schedule');
    }
  };

  const handleExpandSchedule = async (scheduleId) => {
    if (expandedScheduleId === scheduleId) {
      setExpandedScheduleId(null);
      return;
    }

    if (!runHistory[scheduleId]) {
      setExpandedScheduleId(scheduleId);
      setLoadingRuns(prev => ({ ...prev, [scheduleId]: true }));
      try {
        const response = await getScheduleRunHistory(organizationId, scheduleId);
        // The response is { success: true, data: { recentRuns, nextScheduledRuns, ... } }
        const historyData = response?.data?.data;
        setRunHistory(prev => ({ ...prev, [scheduleId]: historyData }));
        console.log(`Run history for schedule ${scheduleId}:`, historyData);
      } catch (err) {
        console.error(`Error fetching run history for schedule ${scheduleId}:`, err);
        toast.error('Failed to load run history. Please try again.');
      } finally {
        setLoadingRuns(prev => ({ ...prev, [scheduleId]: false }));
      }
    } else {
      setExpandedScheduleId(scheduleId);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100 border-green-200';
      case 'paused': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'deleted': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-3 w-3" />;
      case 'paused': return <Pause className="h-3 w-3" />;
      case 'deleted': return <AlertCircle className="h-3 w-3" />;
      default: return <AlertCircle className="h-3 w-3" />;
    }
  };

  const getRunStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'text-green-600 bg-green-50 border-green-200';
      case 'FAILED': return 'text-red-600 bg-red-50 border-red-200';
      case 'SCHEDULED': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'RUNNING': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatDateInTimezone = (dateString, timezone) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(date);
    } catch (e) {
      return new Date(dateString).toLocaleString();
    }
  };

  if (!organizationData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#CC457D] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <TopBar />
        
        {/* Schedules Content */}
        <div className="flex-1 overflow-auto bg-background">
          <div className="max-w-5xl mx-auto p-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-8">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-pink to-brand-blue-dark">
                <Calendar className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-foreground">
                  Scheduled Analytics
                </h1>
                <p className="text-xs text-grey mt-0.5">
                  {schedules.length} automated {schedules.length === 1 ? 'schedule' : 'schedules'}
                </p>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-3 bg-red-50 border-l-4 border-red-400 rounded-r">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Loading State */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3 text-grey">
                  <div className="w-4 h-4 border-2 border-grey border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm">Loading...</span>
                </div>
              </div>
            ) : (
              /* Schedules List */
              <div className="space-y-3">
                {schedules.length === 0 ? (
                  <div className="text-center py-12">
                    {/* Icon */}
                    <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-brand-blue-light/20 flex items-center justify-center">
                      <Calendar className="h-8 w-8 text-brand-blue-dark opacity-50" />
                    </div>
                    
                    {/* Text */}
                    <h3 className="text-sm font-medium text-foreground mb-1">No scheduled threads</h3>
                    <p className="text-xs text-grey mb-6 max-w-xs mx-auto">
                      Run a new thread or schedule an existing one for recurring automation
                    </p>
                    
                    {/* Link */}
                    <Link
                      to={`/organization/${organizationId}/threads`}
                      className="inline-flex items-center gap-1.5 text-sm text-brand-pink hover:text-brand-pink/80 font-medium transition-colors"
                    >
                      Go to Threads
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                ) : (
                  schedules.map((schedule) => (
                    <div key={schedule._id}>
                      <div
                        className="group bg-white border border-border rounded-xl p-4 hover:bg-gray-50/50 transition-all duration-200"
                      >
                        <div className="flex items-center justify-between gap-4">
                          {/* Main Content */}
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-2 h-2 rounded-full bg-brand-pink flex-shrink-0"></div>
                            
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate mb-0.5">
                                {schedule.thread?.prompt || schedule.payload?.prompt}
                              </p>
                              <div className="flex items-center gap-3 text-xs text-grey">
                                <span>{schedule.description}</span>
                                <span>•</span>
                                <span>{schedule.thread?.agentType}</span>
                                {schedule.thread?.config?.rolling?.days && (
                                  <>
                                    <span>•</span>
                                    <span>{schedule.thread.config.rolling.days}d rolling</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Status & Actions */}
                          <div className="flex items-center gap-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${getStatusColor(schedule.status)}`}>
                              {getStatusIcon(schedule.status)}
                              {schedule.status || 'active'}
                            </span>
                            
                            <span className="text-xs text-grey">
                              {new Date(schedule.created_at).toLocaleDateString([], {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>

                            <button
                              onClick={() => handleExpandSchedule(schedule._id)}
                              className="p-1.5 text-grey hover:text-foreground hover:bg-gray-100 rounded-lg transition-all"
                              title="View run history"
                            >
                              {expandedScheduleId === schedule._id ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </button>
                            
                            <button
                              onClick={() => handleDeleteSchedule(schedule._id)}
                              className="opacity-0 group-hover:opacity-100 p-1.5 text-grey hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                              title="Delete schedule"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {expandedScheduleId === schedule._id && (
                        <div className="bg-gray-50 border border-t-0 border-border rounded-b-xl p-4 space-y-4">
                          {loadingRuns[schedule._id] ? (
                            <div className="flex items-center justify-center py-12">
                              <div className="flex flex-col items-center gap-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-4 h-4 border-2 border-brand-pink border-t-transparent rounded-full animate-spin"></div>
                                  <span className="text-sm font-medium text-foreground">Loading run history...</span>
                                </div>
                                <p className="text-xs text-grey">Fetching execution data</p>
                              </div>
                            </div>
                          ) : !runHistory[schedule._id] ? (
                            <div className="text-center py-8">
                              <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-red-100 mb-3">
                                <AlertCircle className="h-5 w-5 text-red-600" />
                              </div>
                              <p className="text-grey text-sm font-medium">Failed to load run history</p>
                              <p className="text-grey text-xs mt-1">Please try again or contact support</p>
                              <button
                                onClick={() => {
                                  setRunHistory(prev => {
                                    const copy = { ...prev };
                                    delete copy[schedule._id];
                                    return copy;
                                  });
                                  handleExpandSchedule(schedule._id);
                                }}
                                className="mt-3 text-xs text-brand-pink hover:text-brand-pink/80 font-medium"
                              >
                                Retry
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-6">
                              {/* Recent Runs */}
                              <div>
                                <h4 className="text-xs font-semibold text-foreground mb-3 uppercase tracking-wider">Recent Runs</h4>
                                <div className="space-y-2">
                                  {runHistory[schedule._id]?.recentRuns && runHistory[schedule._id].recentRuns.length > 0 ? (
                                    runHistory[schedule._id].recentRuns.map((run, idx) => (
                                      <div key={idx} className="bg-white border border-border rounded-lg p-3 text-xs">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getRunStatusColor(run.status)}`}>
                                            {run.status}
                                          </span>
                                        </div>
                                        <div className="space-y-1 text-grey text-xs">
                                          <p><strong>Scheduled:</strong> {formatDateInTimezone(run.scheduledAt, runHistory[schedule._id].timezone)}</p>
                                          <p><strong>Executed:</strong> {formatDateInTimezone(run.takenAt, runHistory[schedule._id].timezone)}</p>
                                          {run.result?.error && (
                                            <p className="text-red-600 mt-2"><strong>Error:</strong> {run.result.error}</p>
                                          )}
                                          {run.result?.failedAt && (
                                            <p className="text-red-600 text-xs"><strong>Failed at:</strong> {formatDateInTimezone(run.result.failedAt, runHistory[schedule._id].timezone)}</p>
                                          )}
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <p className="text-grey text-xs">No recent runs</p>
                                  )}
                                </div>
                              </div>

                              {/* Next Scheduled Runs */}
                              <div>
                                <h4 className="text-xs font-semibold text-foreground mb-3 uppercase tracking-wider">Upcoming Runs</h4>
                                <div className="space-y-2">
                                  {runHistory[schedule._id]?.nextScheduledRuns && runHistory[schedule._id].nextScheduledRuns.length > 0 ? (
                                    runHistory[schedule._id].nextScheduledRuns.map((run, idx) => (
                                      <div key={idx} className="bg-white border border-border rounded-lg p-3 text-xs">
                                        <div className="flex items-center justify-between">
                                          <p className="text-foreground font-medium">
                                            {formatDateInTimezone(run.scheduledAt, runHistory[schedule._id].timezone)}
                                          </p>
                                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getRunStatusColor(run.status)}`}>
                                            {run.status}
                                          </span>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <p className="text-grey text-xs">No scheduled runs</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main component that provides the organization context
const AlertsPage = () => {
  return (
    <OrganizationProvider>
      <AlertsPageContent />
    </OrganizationProvider>
  );
};

export default AlertsPage;